import React from 'react';

import { Trans } from '@lingui/react/macro';
import { PermPhoneMsg } from '@mui/icons-material';
import ContactSupportOutlined from '@mui/icons-material/ContactSupportOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import type { Theme } from '@mui/material';
import { Divider, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { useLocation } from 'react-router-dom';

import {
  blueBlack400,
  blueBlack500,
  blueBlack630,
  white,
} from '../../../theme/videa-style-tokens';
import { useAppContext } from '../../../utils/hooks/useAppContext';
import type { EventName } from '../../../utils/mixpanel';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import {
  useCheckIfDentalCorpPractice,
  useCheckIfHeartlandPractice,
  useCheckIfSagePractice,
} from '../../../utils/user';
import { parsePathname } from '../path-utils';

import type { CopyTextEvent, OpenWidgetEvent } from './types';
import { CustomerSupportInfoTypes } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  buttonText: {
    color: blueBlack500,
    fontSize: '1em',
    fontFamily: 'proxima-nova, sans-serif',
    lineHeight: '1',
    textTransform: 'none',
  },
  popoverText: {
    fontFamily: 'proxima-nova, sans-serif',
    fontSize: '.875em',
    textAlign: 'left',
    color: 'white',
    paddingBottom: '5px',
    display: 'block',
  },
  phoneIcon: {
    color: blueBlack400,
    fontSize: '1.25em',
  },
  widget: {
    fontFamily: 'proxima-nova, sans-serif',
    padding: '0.25em',
    borderRadius: '1.5em',
    fontVariantNumeric: 'tabular-nums',
    color: blueBlack500,
    minWidth: '2em',
    '&:hover': {
      backgroundColor: white,
    },
  },
  popoverPaper: {
    padding: theme.spacing(1),
    boxShadow: '0 .25em .5em ${greyLight600}',
    borderRadius: '12px',
    background: `linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.125) 100%) ${blueBlack630}`,
    maxWidth: '320px',
  },
  copyIcon: {
    color: 'rgba(255, 255, 255, 0.54)',
    '&.Mui-focusVisible': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&:active': {
      color: 'rgba(255, 255, 255, 0.2)',
    },
  },
  contactIcon: {
    color: 'white',
    marginLeft: '8px',
  },
  divider: {
    border: '1px solid rgba(0, 0, 0, 0.12)',
    width: '100%',
    marginBottom: '10px',
  },
}));

const ContactSupportWidget: React.FC = () => {
  const { trackEvent } = useTrackEvent();
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const isHeartland = useCheckIfHeartlandPractice();

  const isSage = useCheckIfSagePractice();

  const isDentalCorp = useCheckIfDentalCorpPractice();

  const { pathname } = useLocation();
  const { isAuthenticated } = useAppContext();

  if (!isAuthenticated) return null;

  let email = 'support@videa.ai';
  let phoneNumber = '+1 (508) 684-5644';

  if (isHeartland) {
    email = 'itsupport@heartland.com';
    phoneNumber = '(217) 540-6090';
  }
  if (isSage) {
    email = 'http://support.mysagedental.com';
    phoneNumber = '(561) 998-0544';
  }
  if (isDentalCorp) {
    email = 'it.support@dentalcorp.ca';
    phoneNumber = '1-833-511-DENT (3368)';
  }

  const isOpen = Boolean(anchorEl);

  const triggerMixpanelEvent = (
    type: EventName,
    data: OpenWidgetEvent | CopyTextEvent,
  ) => {
    trackEvent(type, {
      ...data,
    });
  };

  const handleWidgetClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isOpen) {
      const { currentPage, patientId, imageId } = parsePathname(pathname);
      triggerMixpanelEvent(mixpanelEvents.customerSupportWidgetOpen, {
        currentPage: currentPage,
        patientId: patientId,
        // @ts-expect-error TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
        imageId: imageId,
      });
    }
    setAnchorEl(event.currentTarget);
  };

  const handleWidgetClose = () => {
    setAnchorEl(null);
  };

  const handleCopyText = (
    copiedText: string,
    copiedTextType: CustomerSupportInfoTypes,
  ) => {
    navigator.clipboard.writeText(copiedText);
    triggerMixpanelEvent(mixpanelEvents.customerSupportInformationCopy, {
      copiedValue: copiedTextType,
    });
  };

  return (
    <ClickAwayListener onClickAway={handleWidgetClose}>
      <Box>
        <Button
          className={`app-header-button ${classes.widget}`}
          onClick={handleWidgetClick}
          data-testid="cs-widget"
        >
          <ContactSupportOutlined className={classes.phoneIcon} />
        </Button>
        <Popover
          id={isOpen ? 'simple-popover' : undefined}
          open={isOpen}
          classes={{
            paper: classes.popoverPaper,
          }}
          onClose={handleWidgetClose}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 35,
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box display="block" padding="10px 12px" align-items="center">
            <Typography className={classes.popoverText}>
              <Trans>
                We can help with getting set up, questions about the findings or
                interface, or whatever support you need.
              </Trans>
            </Typography>
          </Box>
          <Divider className={classes.divider} />
          <Box
            display="flex"
            flex-direction="row"
            gap="35px"
            justifyContent="flex-start"
            alignItems="center"
            width="100%"
            padding="4px"
          >
            <PermPhoneMsg className={classes.contactIcon} />
            <Box
              display="flex"
              flex-direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              marginRight="8px"
            >
              <Typography className={classes.popoverText}>
                {phoneNumber}
              </Typography>
              <IconButton
                aria-label="copy"
                onClick={() =>
                  handleCopyText(
                    phoneNumber,
                    CustomerSupportInfoTypes.phoneNumber,
                  )
                }
              >
                <ContentCopyIcon className={classes.copyIcon} />
              </IconButton>
            </Box>
          </Box>

          <Box
            display="flex"
            flex-direction="row"
            gap="35px"
            justifyContent="flex-start"
            alignItems="center"
            width="100%"
            padding="4px"
          >
            <MailOutlineIcon className={classes.contactIcon} />
            <Box
              display="flex"
              flex-direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              marginRight="8px"
            >
              <Typography className={classes.popoverText}>{email}</Typography>

              <IconButton
                aria-label="copy"
                onClick={() =>
                  handleCopyText(email, CustomerSupportInfoTypes.email)
                }
              >
                <ContentCopyIcon className={classes.copyIcon} />
              </IconButton>
            </Box>
          </Box>
        </Popover>
      </Box>
    </ClickAwayListener>
  );
};

export default ContactSupportWidget;
